import React, { useEffect, useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { useStrings } from '@/hooks/useStrings';
import { useScreenTransition } from '@/hooks/useScreenTransition';
import { safeWrite } from '@/utils/supabaseWrite';
import { hapticTap, hapticMilestone } from '@/utils/haptics';
import { playSessionCompleteChime } from '@/utils/sound';
import OnboardingHeader from '@/components/shared/OnboardingHeader';
import styles from './ProgramReveal.module.css';

// Tiny rose dot field for the "Begin Day 1" celebration. Each dot has
// a deterministic offset (--sx, --sy) and stagger delay (--sd) so the
// burst reads as a soft outward field rather than a single ping.
// Position values are tuned for a ~120x120 hero icon footprint so the
// sparkles cluster around the centre, then drift outward and fade.
const SPARKLES = [
  { sx: -36, sy: -28, sd: 0 },
  { sx: 30,  sy: -34, sd: 80 },
  { sx: -42, sy: 12,  sd: 160 },
  { sx: 38,  sy: 18,  sd: 60 },
  { sx: -10, sy: -44, sd: 140 },
  { sx: 14,  sy: 32,  sd: 220 },
  { sx: -28, sy: 30,  sd: 180 },
  { sx: 44,  sy: -8,  sd: 100 },
];

function SparkleField() {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: 0,
        height: 0,
        pointerEvents: 'none',
      }}
    >
      {SPARKLES.map((s, i) => (
        <span
          key={i}
          className="onboard-sparkle"
          style={{ '--sx': `${s.sx}px`, '--sy': `${s.sy}px`, '--sd': `${s.sd}ms` }}
        />
      ))}
    </div>
  );
}

// Active screens, in order. Slot 2 was originally a "how" screen
// (10 mins, 3x a week — redundant with screen 1's phase descriptions).
// It's now reused for a Day-1 session preview so users land on the
// session feeling prepared instead of wondering what they're walking
// into. Slot 3 is the final celebration / Begin Day 1.
const ACTIVE_SCREENS = [0, 1, 2, 3];
const TOTAL_SCREENS = ACTIVE_SCREENS.length;

// ── Inline icons (stroke #A8003F, strokeWidth 1.6, round) ──────────────

const iconProps = {
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

function IconCalendarSpiral() {
  return (
    <svg style={{ color: 'var(--primary)' }} {...iconProps} aria-hidden="true">
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M8 3.5v3" />
      <path d="M16 3.5v3" />
      <path d="M3.5 10h17" />
      <path d="M12 13.5a2 2 0 1 1-1.6 3.2" />
    </svg>
  );
}

function IconHeart() {
  return (
    <svg style={{ color: 'var(--primary)' }} {...iconProps} aria-hidden="true">
      <path d="M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.5-7 10-7 10z" />
    </svg>
  );
}

function IconSeed() {
  return (
    <svg style={{ color: 'var(--primary)' }} {...iconProps} aria-hidden="true">
      <path d="M12 21v-7" />
      <path d="M12 14c-3 0-5-2-5-5 3 0 5 2 5 5z" />
      <path d="M12 14c3 0 5-2 5-5-3 0-5 2-5 5z" />
      <path d="M9 21h6" />
    </svg>
  );
}

function IconTree() {
  return (
    <svg style={{ color: 'var(--primary)' }} {...iconProps} aria-hidden="true">
      <path d="M12 3l-5 7h3l-3 5h3l-3 5h10l-3-5h3l-3-5h3z" />
      <path d="M12 20v2" />
    </svg>
  );
}

function IconStar() {
  return (
    <svg style={{ color: 'var(--primary)' }} {...iconProps} aria-hidden="true">
      <path d="M12 3l2.6 5.6 6.1.6-4.6 4.2 1.3 6-5.4-3.1-5.4 3.1 1.3-6L3.3 9.2l6.1-.6z" />
    </svg>
  );
}

function IconSpark() {
  return (
    <svg style={{ color: 'var(--primary)' }} {...iconProps} aria-hidden="true">
      <path d="M12 3v4" />
      <path d="M12 17v4" />
      <path d="M3 12h4" />
      <path d="M17 12h4" />
      <path d="M5.5 5.5l2.8 2.8" />
      <path d="M15.7 15.7l2.8 2.8" />
      <path d="M5.5 18.5l2.8-2.8" />
      <path d="M15.7 8.3l2.8-2.8" />
    </svg>
  );
}

function IconClock() {
  return (
    <svg style={{ color: 'var(--primary)' }} {...iconProps} aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function IconRings() {
  return (
    <svg style={{ color: 'var(--primary)' }} {...iconProps} aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function IconFootprint() {
  return (
    <svg style={{ color: 'var(--primary)' }} {...iconProps} aria-hidden="true">
      <path d="M9 3c2 0 3 2 3 5s-1 6-3 6-3-3-3-6 1-5 3-5z" />
      <circle cx="6.5" cy="16" r="1.3" />
      <circle cx="9.5" cy="19" r="1.3" />
      <circle cx="12" cy="16.5" r="1.3" />
    </svg>
  );
}

// ── Bullet card ────────────────────────────────────────────────────────

const bulletStyles = {
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 8,
    marginBottom: 16,
    flex: 1,
  },
  card: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    background: 'var(--surface, #FFFFFF)',
    border: '1px solid var(--border, #E8DDD4)',
    borderRadius: 12,
    padding: '14px 16px',
  },
  icon: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
  },
  text: {
    flex: 1,
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 16,
    fontWeight: 400,
    color: 'var(--text, #3D2B1F)',
    lineHeight: 1.55,
  },
  heroIconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '8px 0 20px',
  },
  heroIcon: {
    width: 56,
    height: 56,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    background: 'var(--surface, #FFFFFF)',
    border: '1px solid var(--border, #E8DDD4)',
  },
  phaseIconRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  phaseIcon: {
    flexShrink: 0,
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    background: 'var(--surface, #FFFFFF)',
    border: '1px solid var(--border, #E8DDD4)',
    marginTop: 2,
  },
  phaseBody: {
    flex: 1,
  },
};

function BulletCard({ icon, text, className, style }) {
  return (
    <div className={className} style={{ ...bulletStyles.card, ...style }}>
      <span style={bulletStyles.icon}>{icon}</span>
      <span style={bulletStyles.text}>{text}</span>
    </div>
  );
}

const PHASE_ACCENTS = [
  styles.phaseAccentFoundation,
  styles.phaseAccentIntermediate,
  styles.phaseAccentAdvanced,
];

function PhaseCard({ index, name, days, description, accentClass, icon }) {
  const positionClass = styles[`phaseCard--${index + 1}`];
  return (
    <div className={`${styles.phaseCard} ${positionClass || ''}`}>
      <span className={`${styles.phaseBar} ${accentClass}`} aria-hidden="true" />
      <div className={styles.phaseInner}>
        <div style={bulletStyles.phaseIconRow}>
          {icon ? <span style={bulletStyles.phaseIcon}>{icon}</span> : null}
          <div style={bulletStyles.phaseBody}>
            <div className={styles.phaseName}>{name}</div>
            <div className={styles.phaseDays}>{days}</div>
            <div className={styles.phaseDesc}>{description}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProgramReveal({ onComplete, onClose, onExit }) {
  const { user, userProgress } = useApp();
  const t = useStrings();
  const [currentScreen, setCurrentScreen] = useState(0);
  const [finishing, setFinishing] = useState(false);
  useScreenTransition(currentScreen);

  // Read fresh user_path from localStorage (written by PathAssessment).
  // userProgress in context is not re-fetched between onboarding steps.
  function getUserPath() {
    try {
      const saved = localStorage.getItem('bb7');
      if (saved) {
        const data = JSON.parse(saved);
        if (data?.user_path) return data.user_path;
      }
    } catch {}
    return userProgress?.path || 'A';
  }

  const isPathB = getUserPath() === 'B';

  // Effort tally for the final celebration screen. Counts the
  // upstream onboarding steps the user actually completed so the
  // "Begin Day 1" moment reads as "first chapter done" rather than
  // "intro you survived." Each step writes its own flag earlier in
  // the flow; we read them here from the local cache (the source of
  // truth in the patchProgress pattern).
  function getEffortCount() {
    let data = {};
    try {
      const saved = localStorage.getItem('bb7');
      if (saved) data = JSON.parse(saved);
    } catch {}
    let count = 0;
    if (data.user_intent && data.user_intent !== 'unspecified') count += 1;
    if (data.path_assigned_at || data.user_path) count += 1;
    if (data.foundation_lesson_complete) count += 1;
    if (data.breathing_discovery_complete) count += 1;
    return count;
  }
  const effortCount = getEffortCount();

  // Milestone celebration when the user lands on the final "Begin Day 1"
  // screen — soft chime (no-op when sound is disabled) + 3-pulse haptic.
  // The sparkle field below mounts on render and animates itself.
  useEffect(() => {
    if (currentScreen !== 3) return;
    hapticMilestone();
    playSessionCompleteChime();
  }, [currentScreen]);

  function goNext() {
    hapticTap();
    setCurrentScreen((id) => {
      const i = ACTIVE_SCREENS.indexOf(id);
      if (i < 0 || i >= ACTIVE_SCREENS.length - 1) return id;
      return ACTIVE_SCREENS[i + 1];
    });
  }

  // OnboardingHeader back handler. The first active screen → dismiss
  // the whole flow (→ /home). Any later sub-screen steps back through
  // the active screens.
  function handleBack() {
    if (finishing) return;
    if (currentScreen === ACTIVE_SCREENS[0]) {
      if (onClose) onClose();
      return;
    }
    setCurrentScreen((id) => {
      const i = ACTIVE_SCREENS.indexOf(id);
      if (i <= 0) return id;
      return ACTIVE_SCREENS[i - 1];
    });
  }

  // ProgramReveal is the final onboarding step, so "skip" is
  // functionally the same as "finish": flip onboarding_complete and
  // advance. We still write a skip flag so future reporting can tell
  // completers from skippers.
  async function handleSkip() {
    if (finishing) return;
    setFinishing(true);
    try {
      let data = {};
      try {
        const saved = localStorage.getItem('bb7');
        if (saved) data = JSON.parse(saved);
      } catch {}
      data.onboarding_complete = true;
      data.onboarding_completed_at = new Date().toISOString();
      data.program_reveal_skipped = true;
      data.current_day = 1;
      data.current_year = 1;
      await safeWrite(user?.id, data);
      onComplete();
    } catch (err) {
      console.error('ProgramReveal skip save failed:', err);
      setFinishing(false);
    }
  }

  async function beginDayOne() {
    if (finishing) return;
    hapticMilestone();
    setFinishing(true);
    try {
      let data = {};
      try {
        const saved = localStorage.getItem('bb7');
        if (saved) data = JSON.parse(saved);
      } catch {}
      data.onboarding_complete = true;
      data.onboarding_completed_at = new Date().toISOString();
      data.current_day = 1;
      data.current_year = 1;
      await safeWrite(user?.id, data);
      onComplete();
    } catch (err) {
      console.error('ProgramReveal save failed:', err);
      setFinishing(false);
    }
  }

  const stepIndex = Math.max(0, ACTIVE_SCREENS.indexOf(currentScreen));
  const progressPct = ((stepIndex + 1) / TOTAL_SCREENS) * 100;

  return (
    <div className={styles.screen}>
      <OnboardingHeader onBack={handleBack} onSkip={handleSkip} onExit={onExit} />

      <div
        className={styles.progressTrack}
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={TOTAL_SCREENS}
        aria-valuenow={stepIndex + 1}
      >
        <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
      </div>

      <div className={styles.scroll}>
        <div className={styles.container}>
          {currentScreen === 0 && (
            <div key={currentScreen} className="screen-enter">
              <h1 className={`${styles.title} screen-enter-child`}>{t('pr_reveal_title')}</h1>
              <p className={`${styles.subtitle} screen-enter-child`}>{t('pr_reveal_bridge')}</p>
              <div style={bulletStyles.stack} className="screen-enter-child">
                <BulletCard
                  icon={<IconCalendarSpiral />}
                  text={t('pr_reveal_bullet_1')}
                  className="stagger-item"
                  style={{ '--stagger-i': 0 }}
                />
                <BulletCard
                  icon={<IconHeart />}
                  text={t('pr_reveal_bullet_2')}
                  className="stagger-item"
                  style={{ '--stagger-i': 1 }}
                />
              </div>
            </div>
          )}

          {currentScreen === 1 && (
            <div key={currentScreen} className="screen-enter">
              <h1 className={`${styles.title} screen-enter-child`}>{t('pr_phases_title')}</h1>
              <div className={`${styles.phases} screen-enter-child`}>
                <PhaseCard
                  index={0}
                  name={t('pr_phase1_name')}
                  days={t('pr_phase1_days')}
                  description={isPathB ? t('pr_phase1_desc_b') : t('pr_phase1_desc_a')}
                  accentClass={PHASE_ACCENTS[0]}
                  icon={<IconSeed />}
                />
                <PhaseCard
                  index={1}
                  name={t('pr_phase2_name')}
                  days={t('pr_phase2_days')}
                  description={t('pr_phase2_desc')}
                  accentClass={PHASE_ACCENTS[1]}
                  icon={<IconTree />}
                />
                <PhaseCard
                  index={2}
                  name={t('pr_phase3_name')}
                  days={t('pr_phase3_days')}
                  description={t('pr_phase3_desc')}
                  accentClass={PHASE_ACCENTS[2]}
                  icon={<IconStar />}
                />
              </div>
            </div>
          )}

          {currentScreen === 2 && (
            <div key={currentScreen} className="screen-enter">
              <h1 className={`${styles.title} screen-enter-child`}>{t('pr_preview_title')}</h1>
              <p className={`${styles.subtitle} screen-enter-child`}>{t('pr_preview_subtitle')}</p>
              <div style={bulletStyles.stack} className="screen-enter-child">
                <BulletCard
                  icon={<IconClock />}
                  text={isPathB ? t('pr_preview_bullet_time_b') : t('pr_preview_bullet_time_a')}
                  className="stagger-item"
                  style={{ '--stagger-i': 0 }}
                />
                <BulletCard
                  icon={<IconRings />}
                  text={isPathB ? t('pr_preview_bullet_what_b') : t('pr_preview_bullet_what_a')}
                  className="stagger-item"
                  style={{ '--stagger-i': 1 }}
                />
                <BulletCard
                  icon={<IconHeart />}
                  text={t('pr_preview_bullet_job')}
                  className="stagger-item"
                  style={{ '--stagger-i': 2 }}
                />
              </div>
            </div>
          )}

          {currentScreen === 3 && (
            <div key={currentScreen} className="screen-enter">
              <div style={{ position: 'relative' }}>
                {/* Day-1 celebration: warm bloom + sparkle field behind
                    the title. Both are aria-hidden one-shot animations. */}
                <span className="onboard-bloom" aria-hidden="true" />
                <SparkleField />
                {/* Effort tally — tells the user what they already did
                    so the final screen reads as "first chapter done"
                    rather than "intro you survived." Reads localStorage
                    flags written by earlier onboarding steps. */}
                <p className={`${styles.eyebrowSummary} screen-enter-child`}>
                  {t('pr_begin_summary').replace('{count}', String(effortCount))}
                </p>
                <h1 className={`${styles.title} screen-enter-child`}>{t('pr_begin_title')}</h1>
              </div>
              <p className={`${styles.subtitle} screen-enter-child`}>{t('pr_begin_subtitle')}</p>
              <div style={bulletStyles.stack} className="screen-enter-child">
                <BulletCard
                  icon={<IconSpark />}
                  text={t('pr_begin_bullet_1')}
                  className="stagger-item"
                  style={{ '--stagger-i': 0 }}
                />
                <BulletCard
                  icon={<IconFootprint />}
                  text={t('pr_begin_bullet_2')}
                  className="stagger-item"
                  style={{ '--stagger-i': 1 }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={styles.footer}>
        {currentScreen === 0 && (
          <button type="button" className={`${styles.cta} motion-breath`} onClick={goNext}>
            {t('pr_reveal_cta')}
          </button>
        )}

        {currentScreen === 1 && (
          <button type="button" className={`${styles.cta} motion-breath`} onClick={goNext}>
            {t('pr_phases_cta')}
          </button>
        )}

        {currentScreen === 2 && (
          <button type="button" className={`${styles.cta} motion-breath`} onClick={goNext}>
            {t('pr_preview_cta')}
          </button>
        )}

        {currentScreen === 3 && (
          <button
            type="button" className={finishing ? styles.cta : `${styles.cta} motion-breath`}
            onClick={beginDayOne}
            disabled={finishing}
          >
            {t('pr_begin_cta')}
          </button>
        )}
      </div>
    </div>
  );
}
